import * as React from "react";
import {Suspense, useState} from "react";
import {Button, Carousel, Col, Container, Form, Image, InputGroup, Modal, Row} from "react-bootstrap";
import {
    FaCheck,
    FaChevronCircleLeft,
    FaChevronCircleRight,
    FaPlusCircle,
    FaRegThumbsUp,
    FaSearch,
    FaStar
} from "react-icons/fa";
import {RatingDistribution} from "./ratingDistribution";
import {Media as RatingMedia} from "./media";
import {useProductRatingsQuery} from "../../../hooks/useProductRatingsQuery";
import {StarRating} from "../../../components/starRating";
import {Comments} from "./comments";
import {RatingModel} from "../../../models/domain/ratingModel";
import {RatingReviewMediaModel} from "../../../models/domain/ratingReviewMediaModel";
import {IRatingModalProps, useRatingModal} from "../../../hooks/useRatingModal";
import {ErrorBoundary} from "@sentry/react";
import {RatingPaginationSortType} from "../../../definitions/enums";
import {SkeletonAccordionBody} from "../../../components/skeletonAccordionBody";
import {useRatingPaginationVariables} from "../../../hooks/useRatingPaginationVariables";
import {AiFillCaretDown, AiFillCaretUp, AiOutlineCloseCircle} from "react-icons/ai";
import {SkeletonCarousel} from "../../../components/skeletonCarousel";
import {useReviewFeedbackMutation} from "../../../hooks/useReviewFeedbackMutation";
import {useRefsContext} from "../../../utils/refContext";
import {useViewMoreText} from "../../../hooks/useViewMoreText";
import {PagingStatus} from "./pagingStatus";
import {ShowMoreButtonText} from "./showMoreButtonText";
import {ZeroRatings} from "./zeroRatings";
import {WriteReviewButton} from "./writeReviewButton";

const gradeFilterOptions: (1 | 2 | 3 | 4 | 5)[] = [5, 4, 3, 2, 1];
const sortByOptions: { key: string, value: RatingPaginationSortType }[] = [{
    key: 'Newest',
    value: RatingPaginationSortType.ReverseChronological
}, {
    key: 'Oldest',
    value: RatingPaginationSortType.Chronological
}, {
    key: 'Highest Rated',
    value: RatingPaginationSortType.HighestRated
}, {
    key: 'Lowest Rated',
    value: RatingPaginationSortType.LowestRated
}, {
    key: 'Most helpful',
    value: RatingPaginationSortType.MostHelpful
}, {
    key: 'Least helpful',
    value: RatingPaginationSortType.LeastHelpful
}, {
    key: 'Photo Reviews',
    value: RatingPaginationSortType.UserContentCountDesc
}];

export const ProductRatings = () => {
    const {ratingFilterContainerRef} = useRefsContext();
    const {
        variables: {
            sortBy,
            search: {
                term,
                searchedTerm,
                hasTerm,
                isButtonDisabled
            }
        },
        mutations: {
            search,
            setSearchTerm,
            clearSearchTerm,
            setSortBy,
            filters: {
                zeroFilters,
                hasFilters,
                activeStar,
                toggleStar,
                clear,
                hasGradeFilters,
                gradeFilters
            }
        },
        pagination: {
            isDefault
        }
    } = useRatingPaginationVariables();

    return (
        <>
            <div style={{minHeight: '150px'}} className="pb-2 gx-0">
                <Row className="pb-3 border-bottom border-5">
                    <ErrorBoundary fallback={<></>}>
                        <Col lg={8} className="mb-3">
                            <RatingDistribution/>
                        </Col>
                        <Col lg={4} className="d-flex align-items-center my-3 my-lg-0">
                            <div>
                                <span className="fs-5">We'd love to hear your Experience.</span>
                                <p>
                                    Write a review and help others make informed decisions!
                                </p>
                                <WriteReviewButton/>
                            </div>
                        </Col>
                    </ErrorBoundary>
                    <Suspense fallback={<SkeletonCarousel/>}>
                        <ErrorBoundary fallback={<></>}>
                            <RatingMedia/>
                        </ErrorBoundary>
                    </Suspense>

                </Row>
                <div ref={ratingFilterContainerRef} className="d-flex align-items-end my-3 flex-wrap flex-md-nowrap">
                    <InputGroup style={{maxWidth: '270px'}}>
                        <Form.Control type='text'
                                      className='form-control border-end-0'
                                      placeholder='Search ratings'
                                      style={{maxWidth: '270px'}}
                                      value={term}
                                      onChange={({target: {value}}) => setSearchTerm(value)}
                                      onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                              e.preventDefault();
                                              search();
                                          }
                                      }}
                        />
                        <Button
                            variant="link"
                            className="border border-start-0 opacity-100"
                            disabled={isButtonDisabled}
                            onClick={search}
                            aria-label="Submit Search" // Add a descriptive label for screen readers
                        >
                            <FaSearch aria-hidden/>
                        </Button>
                    </InputGroup>
                    <div className="d-flex flex-wrap align-items-center ms-md-3  mt-3 mt-md-0">
                        <div className="text-muted flex-nowrap me-1" style={{whiteSpace: 'nowrap'}}>
                            Filter by:
                        </div>
                        <div className="d-flex flex-wrap w-100 align-items-center">
                            {gradeFilterOptions.map((grade, index) => (
                                <div key={index} className="text-center me-1 me-sm-2">
                                    <Button
                                        variant="link"
                                        className={`bg-white px-2 px-sm-3 w-100 border d-flex flex-nowrap  border-2 ${activeStar(grade) && 'border-primary fw-bold'}`}
                                        active={activeStar(grade)}
                                        onClick={() => toggleStar(grade)}
                                    >
                                        <FaStar className="me-1 mb-1 text-warning" size={18} aria-hidden/>
                                        {grade}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <Row className="bg-light py-3 d-flex align-items-center ">
                    {(hasFilters || hasGradeFilters) &&
                        <Col xs={12} className="mb-3">
                            <div className="flex-wrap d-flex align-items-center mb-3 mb-sm-0 d-flex align-items-center">
                                {hasFilters && (
                                    <>
                                        {hasTerm && (
                                            <Button variant="link"
                                                    className="my-1 me-2 bg-white border review-filters-btn d-flex align-items-center flex-nowrap"
                                                    onClick={clearSearchTerm}>
                                                {searchedTerm}
                                                <AiOutlineCloseCircle size={20} className="ms-1"/>
                                            </Button>
                                        )}
                                        {hasGradeFilters && (
                                            <>
                                                {gradeFilters.map((grade, index) => (
                                                    <Button key={index} variant="link"
                                                            className="d-none d-sm-flex my-1 me-2 bg-white border review-filters-btn align-items-center flex-nowrap"
                                                            onClick={() => toggleStar(grade)}>
                                                        <FaStar className="me-1 mb-1 text-warning" aria-hidden/>
                                                        {grade}
                                                        <AiOutlineCloseCircle size={20} className="ms-1"/>
                                                    </Button>
                                                ))}
                                            </>
                                        )}
                                        <Button
                                            disabled={zeroFilters}
                                            onClick={() => clear()}>
                                            Clear filters
                                        </Button>
                                    </>
                                )}
                            </div>
                        </Col>
                    }

                    <Col sm={8}>
                        {!isDefault && (<PagingStatus/>)}
                    </Col>
                    <Col sm={4}>
                        <div className="w-100">
                            <Form>
                                <Form.Label htmlFor="sortBySelect" className="m-0"><small>Select Sorting Option</small></Form.Label>
                                <Form.Select
                                    id="sortBySelect"
                                    value={sortBy}
                                    onChange={({target: {value}}) => setSortBy(value)}
                                    aria-label="Select Sorting Option"
                                >
                                    {sortByOptions.map(({key, value}, index) => (
                                        <option key={index} value={value}>
                                            {key}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form>
                        </div>
                    </Col>

                </Row>
                <ErrorBoundary fallback={<ZeroRatings/>}>
                    <Ratings/>
                </ErrorBoundary>
            </div>
        </>
    )
}

const Ratings = () => {
    const [activeMedium, setActiveMedium] = useState<RatingReviewMediaModel | undefined>(undefined);
    const {
        model: {paginationModel: {ratings, pagination}},
        loading,
        fetchNextPage,
        fetchNextPageLoading
    } = useProductRatingsQuery();

    if (loading) return <SkeletonAccordionBody/>

    return (
        <>
            {ratings.length === 0 ?
                <div className="py-3">
                    Sorry, we couldn't find anything! Try adjusting or clearing your filters.
                </div>
                :
                ratings.map((rating) =>
                    <Rating
                        key={String(rating.id)}
                        rating={rating}
                        onMediumClick={(medium: RatingReviewMediaModel) => setActiveMedium(medium)}
                    />
                )}
            {pagination.hasNextPage && (
                <div className="d-flex flex-column align-items-center">
                    <PagingStatus/>
                    <div>
                        <Button
                            variant="primary"
                            className="my-3 d-flex align-items-center"
                            disabled={fetchNextPageLoading}
                            onClick={fetchNextPage}>
                            <FaPlusCircle size={20} className="me-1" aria-hidden/>
                            <ShowMoreButtonText/>
                        </Button>
                    </div>
                </div>
            )}
            {activeMedium && (
                <RatingModal
                    rating={ratings.find((rating) => rating.id === activeMedium.ratingId)!}
                    medium={activeMedium}
                    onHide={() => setActiveMedium(undefined)}
                />
            )}
        </>
    )
}

interface IRatingProps {
    rating: RatingModel;
    onMediumClick: (medium: RatingReviewMediaModel) => void;
}

const Rating = ({
                    onMediumClick,
                    rating: {
                        id,
                        grade,
                        name,
                        visible,
                        createdOn,
                        hasInternalComments,
                        internalComments,
                        review,
                        attributesModel: {
                            any: anyAttributes,
                            concatenate: attributesString
                        }
                    }
                }: IRatingProps) => {
    const {complete, loading, create} = useReviewFeedbackMutation();

    return (
        <div key={id} className={`pt-3 mt-1 user-rating-box mb-3 ${!visible && ' alert alert-danger'}`}>
            <StarRating grade={grade}/>
            <small>({grade}/5)</small>
            <small>
                <FaCheck className="ms-3 me-1 text-success"/>
                Verified Purchase
            </small>
            {anyAttributes && (
                <div className="ratings-variant-options text-muted d-flex flex-wrap mt-1">
                    <small>
                        {attributesString}
                    </small>
                </div>
            )}
            {review && review.visible && (
                <h5 className="review-title my-2">
                    {review.title}
                </h5>
            )}
            <small className="text-muted d-block mt-1">
                By {name} on {createdOn}
            </small>
            {review && review.visible && (
                <>
                    <ReviewText text={review.text} characterLimit={650}/>
                    {review.hasVisibleMedia && (
                        <div className="d-flex flex-wrap thumbnails-wrap my-4">
                            {review.visibleMedia.map((medium, index) => (
                                <div key={index}
                                     className="customer-image-wrap mx-2"
                                     style={{cursor: 'pointer'}}
                                     onClick={() => onMediumClick(medium)}
                                >
                                    <Image src={medium.url} alt={medium.filename} width={100}/>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
            <div className={`py-2 mt-2 ps-0`}>
                {review && review.visible && (
                    <div>
                        <div className="d-flex align-items-center">
                            {complete ? (
                                <div className="text-success">
                                    <FaRegThumbsUp className="me-1 mb-1" size={18}/>
                                    Thank you!
                                </div>
                            ) : (
                                <Button size="sm" variant="outline-info"
                                        className="bg-white text-info"
                                        disabled={loading}
                                        onClick={() => create({reviewId: review.id})}>
                                    <FaRegThumbsUp className="me-1 mb-1" size={18}/>
                                    Helpful
                                </Button>
                            )}
                            {(review.isHelpful || complete) && (
                                <div className="ms-3">
                                    <small className="text-muted">
                                        <FaRegThumbsUp className="me-1 mb-1"/>
                                        {complete
                                            ? (
                                                <strong>
                                                    {review.helpfulCount + 1} found this helpful
                                                </strong>
                                            ) : (
                                                <>
                                                    {review.helpfulCount} found this helpful
                                                </>
                                            )
                                        }
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className="text-end">
                    {hasInternalComments && <Comments ratingId={id} comments={internalComments}/>}
                </div>
            </div>
        </div>
    )
}

interface ReviewTextProps {
    text: string;
    characterLimit: number;
}

const RatingModal = (props: IRatingModalProps) => {
    const {
        rating: {
            grade,
            gradeOutOfFive,
            name,
            createdOn,
            review,
            attributesModel: {
                any: anyAttributes,
                concatenate: attributesString
            }
        }, medium: {
            url,
            filename
        },
        onHide,
        activeIndex,
        onClick
    } = useRatingModal(props);

    return (
        <Modal
            id="customer-photos-modal"
            show={true}
            onHide={onHide}
            size="xl"
            centered
        >
            <Modal.Header closeButton>
                {review && review.visible && review.title}
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row className="align-items-center g-5">
                        {url && filename && (
                            <Col lg={6} className="carousel-column bg-dark">
                                <Carousel
                                    variant="light"
                                    className="w-100"
                                    activeIndex={activeIndex}
                                    onSelect={onClick}
                                    interval={null}
                                    prevIcon={
                                        <FaChevronCircleLeft
                                            aria-hidden="true"
                                            className="carousel-control-prev-icon text-primary bg-white rounded-circle"
                                            size={40}
                                            style={{filter: "none"}}
                                        />
                                    }
                                    nextIcon={
                                        <FaChevronCircleRight
                                            aria-hidden="true"
                                            className="carousel-control-prev-icon text-primary bg-white rounded-circle"
                                            size={40}
                                            style={{filter: "none"}}
                                        />
                                    }
                                >
                                    {review && review.visibleMedia.map(({id, url, filename}) => (
                                        <Carousel.Item key={String(id)}>
                                            <div className="customer-images-carousel-wrap bg-dark">
                                                <Image src={url} alt={filename} className="img-fluid"/>
                                            </div>
                                        </Carousel.Item>
                                    ))}
                                </Carousel>
                            </Col>
                        )}
                        <Col lg={6}>
                            <div className="py-3 mt-1 user-rating-box" style={{height: "100%"}}>
                                <StarRating grade={grade}/>
                                <small>{gradeOutOfFive}</small>
                                <small>
                                    <FaCheck className="ms-3 me-1 text-success"/>
                                    Verified Purchase
                                </small>
                                {anyAttributes && (
                                    <div className="ratings-variant-options text-muted d-flex flex-wrap mt-1">
                                        <small>
                                            {attributesString}
                                        </small>
                                    </div>
                                )}
                                {review && review.visible && (
                                    <h5 className="review-title my-2">{review.title}</h5>
                                )}
                                <small className="text-muted d-block mt-1">
                                    By {name} on {createdOn}
                                </small>
                                {review && review.visible && <p className="mt-2">{review.text}</p>}
                            </div>
                            {review && review.hasVisibleMedia && (
                                <div>
                                    <strong className="d-block py-3">Images in this review</strong>
                                    <div className="d-flex flex-wrap thumbnails-wrap">
                                        {review.visibleMedia.map(({url, filename}, index) => (
                                            <div
                                                key={index}
                                                className={`customer-image-wrap py-2 ${activeIndex === index ? "active border-primary" : ""}`}
                                                onClick={() => onClick(index)}
                                            >
                                                <Image src={url} alt={filename}/>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </Modal.Body>
        </Modal>
    );
};

const ReviewText = ({text, characterLimit}: ReviewTextProps) => {
    const {visibleText, expanded, showToggle, toggle} = useViewMoreText({text, characterLimit});

    return (
        <div className={`review-paragraph mt-2`}>
            <p className={`${showToggle && !expanded ? 'review-truncate review-fade mb-0' : ''}`}>
                {visibleText}
            </p>
            {showToggle && (
                <Button
                    variant="link"
                    className="d-flex align-items-center fw-bold text-decoration-underline mb-3 mt-0 pt-0 ps-0"
                    onClick={toggle}
                    aria-expanded={expanded}
                >
                    {expanded ? (
                        <>
                            View Less<AiFillCaretUp className="ms-0"/>
                        </>
                    ) : (
                        <>
                            View More<AiFillCaretDown className="ms-0"/>
                        </>
                    )}
                </Button>
            )}
        </div>
    );
};